import { Theme } from '@material-ui/core';
import type { RowBoundary, AreaBoundary, ViewState, Place } from '../types/warehouse.types';
import { getRowId } from '../utils/placeUtils';
import { BaseRenderer } from './base/BaseRenderer';

export interface RowLabelConfig {
  showLabel: boolean;
  labelFormat: (row: RowBoundary) => string;
  labelStrokeWidth: number;
}

interface LabelCacheData {
  labelText: string;
  placeWidth: number;
  // Geometric data for view-dependent recalculation
  bestSide: {
    midpoint: { x: number; y: number };
    direction: { x: number; y: number };
    normal: { x: number; y: number };
    length: number;
    start: { x: number; y: number };
    end: { x: number; y: number };
  };
  bestCorrectedNormal: { x: number; y: number };
  maxFreeSpace: number;
}

export class RowLabelRenderer extends BaseRenderer<Record<string, never>> {
  private cache = new Map<string, LabelCacheData>();

  constructor(theme: Theme) {
    super(theme, {});
  }

  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Calculate distance between two points
   */
  private distance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calculate smallest difference between two angles (handles wrap-around at 2π)
   */
  private angleDifference(angle1: number, angle2: number): number {
    let diff = angle1 - angle2;
    // Normalize to [-π, π]
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;
    return diff;
  }

  /**
   * Find the two longest sides of the row polygon
   */
  private findLongestSides(polygon: Array<{ x: number; y: number }>) {
    const sides = [];

    for (let i = 0; i < polygon.length; i++) {
      const start = polygon[i];
      const end = polygon[(i + 1) % polygon.length];
      const length = this.distance(start, end);

      const midpoint = {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2
      };

      const dx = end.x - start.x;
      const dy = end.y - start.y;
      
      let normalX = -dy;
      let normalY = dx;
      
      const normalLen = Math.sqrt(normalX * normalX + normalY * normalY);
      normalX /= normalLen;
      normalY /= normalLen;

      sides.push({
        start,
        end,
        midpoint,
        length,
        normal: { x: normalX, y: normalY },
        direction: { x: dx / length, y: dy / length }
      });
    }

    sides.sort((a, b) => b.length - a.length);
    return sides.slice(0, 2);
  }

  /**
   * Ensure normal vector points outward from the row center
   */
  private ensureNormalPointsOutward(
    side: { midpoint: { x: number; y: number }; normal: { x: number; y: number } },
    rowCenter: { x: number; y: number }
  ): { x: number; y: number } {
    const toMidpoint = {
      x: side.midpoint.x - rowCenter.x,
      y: side.midpoint.y - rowCenter.y
    };
    const dotProduct = toMidpoint.x * side.normal.x + toMidpoint.y * side.normal.y;

    if (dotProduct < 0) {
      return { x: -side.normal.x, y: -side.normal.y };
    }
    return side.normal;
  }

  /**
   * Calculate ray-line segment intersection distance
   */
  private rayLineIntersection(
    origin: { x: number; y: number },
    direction: { x: number; y: number },
    lineStart: { x: number; y: number },
    lineEnd: { x: number; y: number }
  ): number {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;

    const denominator = direction.x * dy - direction.y * dx;

    if (Math.abs(denominator) < 1e-10) {
      return Infinity;
    }

    const t = ((lineStart.x - origin.x) * dy - (lineStart.y - origin.y) * dx) / denominator;
    const u = ((lineStart.x - origin.x) * direction.y - (lineStart.y - origin.y) * direction.x) / denominator;

    if (t > 0 && u >= 0 && u <= 1) {
      return t;
    }

    return Infinity;
  }

  /**
   * Check if there's free space outside a row side
   */
  private checkFreeSpace(
    midpoint: { x: number; y: number },
    normal: { x: number; y: number },
    currentRow: RowBoundary,
    allRows: RowBoundary[],
    areaBoundaries: AreaBoundary[],
    _maxDistance: number
  ): number {
    let minDistance = Infinity;

    for (const otherRow of allRows) {
      if (otherRow === currentRow) continue;

      for (let i = 0; i < otherRow.polygon.length; i++) {
        const lineStart = otherRow.polygon[i];
        const lineEnd = otherRow.polygon[(i + 1) % otherRow.polygon.length];

        const distance = this.rayLineIntersection(midpoint, normal, lineStart, lineEnd);
        minDistance = Math.min(minDistance, distance);
      }
    }

    for (const area of areaBoundaries) {
      for (let i = 0; i < area.polygon.length; i++) {
        const lineStart = area.polygon[i];
        const lineEnd = area.polygon[(i + 1) % area.polygon.length];

        const distance = this.rayLineIntersection(midpoint, normal, lineStart, lineEnd);
        minDistance = Math.min(minDistance, distance);
      }
    }

    return minDistance;
  }

  public render(
    ctx: CanvasRenderingContext2D,
    rows: RowBoundary[],
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    places: Map<string, Place>,
    areaBoundaries: AreaBoundary[],
    config: RowLabelConfig,
    canvasSize?: { width: number; height: number }
  ): void {
    if (!config.showLabel) return;

    let rowPlaceWidths: Map<string, number> | null = null;

    rows.forEach((row) => {
      if (row.polygon.length < 2) return;

      let labelData = this.cache.get(row.name);

      if (!labelData) {
        // Cache miss - perform heavy calculations
        if (!rowPlaceWidths) {
          rowPlaceWidths = new Map<string, number>();
          for (const place of places.values()) {
            const rId = getRowId(place);
            if (!rowPlaceWidths.has(rId)) {
              rowPlaceWidths.set(rId, place.placeDimensions.width);
            }
          }
        }

        const placeWidth = rowPlaceWidths.get(row.name);
        if (!placeWidth) return;

        const labelText = config.labelFormat(row);
        const longestSides = this.findLongestSides(row.polygon);

        let cx = 0, cy = 0;
        row.polygon.forEach(p => { cx += p.x; cy += p.y; });
        const rowCenter = { x: cx / row.polygon.length, y: cy / row.polygon.length };

        let bestSide = null;
        let bestCorrectedNormal = null;
        let maxFreeSpace = 0;

        for (const side of longestSides) {
          const correctedNormal = this.ensureNormalPointsOutward(side, rowCenter);
          const freeSpace = this.checkFreeSpace(
            side.midpoint,
            correctedNormal,
            row,
            rows,
            areaBoundaries,
            placeWidth * 2
          );

          if (freeSpace > maxFreeSpace) {
            maxFreeSpace = freeSpace;
            bestSide = side;
            bestCorrectedNormal = correctedNormal;
          }
        }

        if (bestSide && bestCorrectedNormal) {
           this.cache.set(row.name, {
             labelText,
             placeWidth,
             bestSide,
             bestCorrectedNormal,
             maxFreeSpace
           });
           labelData = this.cache.get(row.name);
        } else {
           return;
        }
      }

      if (!labelData) return;
      
      const { bestSide, bestCorrectedNormal, maxFreeSpace, placeWidth, labelText } = labelData;
      
      // View-dependent calculation
      const fontSize = Math.max(10, placeWidth * viewState.scale);
      
      ctx.font = `bold ${fontSize}px ${this.theme.typography.fontFamily}`;
      const textMetrics = ctx.measureText(labelText);
      const textWidthWorld = textMetrics.width / viewState.scale;
      
      const sideAngle = Math.atan2(bestSide.direction.y, bestSide.direction.x);
      
      let textAngle = 0;
      let renderFontSize = fontSize;

      if (maxFreeSpace >= textWidthWorld) {
        textAngle = 0;
      } else if (maxFreeSpace >= placeWidth) {
        const criticalAngle = Math.acos(Math.min(1, maxFreeSpace / textWidthWorld));
        const normalAngle = Math.atan2(bestCorrectedNormal.y, bestCorrectedNormal.x);
        const desiredAngle = viewState.rotation;
        let angleDiff = this.angleDifference(desiredAngle, normalAngle);

        if (Math.abs(angleDiff) < criticalAngle) {
          if (angleDiff >= 0) {
            textAngle = normalAngle + criticalAngle;
          } else {
            textAngle = normalAngle - criticalAngle;
          }
        } else {
          textAngle = desiredAngle;
        }
      } else {
        textAngle = sideAngle + Math.PI / 2;
        renderFontSize = fontSize * 0.7;
      }

      let finalAngle = textAngle;
      if (Math.cos(textAngle) < 0) {
        finalAngle += Math.PI;
      }

      const effectiveAngle = finalAngle - viewState.rotation;
      const effectiveDirection = {
        x: Math.cos(effectiveAngle),
        y: Math.sin(effectiveAngle)
      };

      const dotProduct = effectiveDirection.x * bestCorrectedNormal.x +
                        effectiveDirection.y * bestCorrectedNormal.y;

      const finalAlign: CanvasTextAlign = dotProduct > 0 ? 'left' : 'right';

      const r = textWidthWorld / 2;
      const pivot1X = bestSide.midpoint.x - bestSide.direction.x * r;
      const pivot1Y = bestSide.midpoint.y - bestSide.direction.y * r;
      const pivot2X = bestSide.midpoint.x + bestSide.direction.x * r;
      const pivot2Y = bestSide.midpoint.y + bestSide.direction.y * r;

      const textWorldX = finalAlign === 'left' ? pivot1X : pivot2X;
      const textWorldY = finalAlign === 'left' ? pivot1Y : pivot2Y;
      
      // Culling
      if (canvasSize) {
         const screenPos = worldToScreen(textWorldX, textWorldY);
         const buffer = 100;
         if (screenPos.x < -buffer || screenPos.x > canvasSize.width + buffer ||
             screenPos.y < -buffer || screenPos.y > canvasSize.height + buffer) {
             return;
         }
      }

      const textScreenPos = worldToScreen(textWorldX, textWorldY);

      // Draw debug dot
      ctx.save();
      ctx.fillStyle = this.theme.palette.error.main;
      ctx.beginPath();
      ctx.arc(textScreenPos.x, textScreenPos.y, 1.67, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Draw text
      ctx.save();
      ctx.translate(textScreenPos.x, textScreenPos.y);
      ctx.rotate(finalAngle);

      ctx.font = `bold ${renderFontSize}px ${this.theme.typography.fontFamily}`;
      ctx.fillStyle = this.theme.palette.storageUi.textPrimary + '80';
      ctx.strokeStyle = this.theme.palette.storageUi.backgroundPrimary + '66';
      ctx.lineWidth = config.labelStrokeWidth;
      ctx.textAlign = finalAlign;
      ctx.textBaseline = 'middle';

      ctx.strokeText(labelText, 0, 0);
      ctx.fillText(labelText, 0, 0);

      ctx.restore();
    });
  }
}
